// src/components/forms/shared/modal/DeleteConfirmDialog.tsx

import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  CircularProgress,
  Typography,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { Delete as DeleteIcon } from '@mui/icons-material';
import { WarningAmber as WarningIcon } from '@mui/icons-material';

import { ModalCancelButton, FormButton } from '../../../common/Buttons';
import DialogBase from '../../../common/modal/DialogBase';

const COUNTDOWN_SECONDS = 6;

interface DeleteConfirmDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  isDeleting: boolean;
  title: string;
  message: React.ReactNode;
}

const DeleteConfirmDialog: React.FC<DeleteConfirmDialogProps> = ({
  open,
  onClose,
  onConfirm,
  isDeleting,
  title,
  message,
}) => {
  const [countdown, setCountdown] = useState(COUNTDOWN_SECONDS);
  const [animKey, setAnimKey] = useState(0);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  useEffect(() => {
    if (open) {
      setCountdown(COUNTDOWN_SECONDS);
      setAnimKey(0);
      intervalRef.current = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            if (intervalRef.current) clearInterval(intervalRef.current);
            return 0;
          }
          return prev - 1;
        });
        setAnimKey((k) => k + 1);
      }, 1000);
    } else {
      if (intervalRef.current) clearInterval(intervalRef.current);
      setCountdown(COUNTDOWN_SECONDS);
    }
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [open]);

  const ready = countdown === 0;
  const showButton = ready || isDeleting;

  const footer = (
    <>
      <ModalCancelButton
        onClick={onClose}
        disabled={isDeleting}
        sx={{
          fontWeight: 600,
          fontSize: '1.8rem',
          backgroundColor: theme.palette.secondary.light,
          color: theme.palette.secondary.main,
          borderColor: theme.palette.secondary.main,
          '& .MuiButton-startIcon': {
            ...(isMobile && { display: 'none' }),
            '& > svg': { color: 'secondary.main' },
          },
          '&:hover': {
            backgroundColor: 'secondary.main',
            color: theme.palette.grey[0],
            borderColor: theme.palette.secondary.main,
            letterSpacing: 'normal',
            '& .MuiButton-startIcon > svg': {
              color: theme.palette.grey[0],
            },
          },
          '@media (max-width: 600px)': {
            minWidth: '130px',
          },
        }}
      />

      <Box
        sx={{
          display: 'grid',
          '& > *': { gridArea: '1 / 1' },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: `2px dashed ${theme.palette.secondary.main}`,
            backgroundColor: theme.palette.secondary.light,
            visibility: showButton ? 'hidden' : 'visible',
            px: 2,
            overflow: 'hidden',
          }}
        >
          <Typography
            key={animKey}
            sx={{
              fontWeight: 800,
              fontSize: '2.5rem',
              lineHeight: '2rem',
              color: 'secondary.main',
              fontVariantNumeric: 'tabular-nums',
              animation: 'slideDown 0.4s ease-in',
              '@keyframes slideDown': {
                '0%': {
                  opacity: 0,
                  transform: 'translateY(-100%)',
                },
                '60%': {
                  opacity: 1,
                  transform: 'translateY(8%)',
                },
                '100%': {
                  opacity: 1,
                  transform: 'translateY(0)',
                },
              },
            }}
          >
            {countdown}
          </Typography>
        </Box>

        <FormButton
          onClick={onConfirm}
          variant="contained"
          color="secondary"
          disabled={isDeleting || !ready}
          startIcon={
            isDeleting ? (
              <CircularProgress size={20} />
            ) : isMobile ? undefined : (
              <DeleteIcon />
            )
          }
          sx={{
            fontWeight: 600,
            fontSize: '1.8rem',
            visibility: showButton ? 'visible' : 'hidden',
            animation:
              showButton && !isDeleting ? 'fadeIn 0.3s ease-in' : 'none',
            '@keyframes fadeIn': {
              from: { opacity: 0 },
              to: { opacity: 1 },
            },
            '&:hover': {
              transform: 'none !important',
              boxShadow: 'none',
              letterSpacing: 'normal',
            },
            '@media (max-width: 600px)': {
              minWidth: '130px',
            },
          }}
        >
          {isDeleting ? 'Deleting...' : 'Delete'}
        </FormButton>
      </Box>
    </>
  );

  return (
    <DialogBase
      open={open}
      onClose={() => !isDeleting && onClose()}
      title={title}
      headerIcon={<WarningIcon />}
      headerColor="secondary"
      footer={footer}
    >
      <Typography
        sx={{
          fontSize: '1.8rem',
          fontWeight: 600,
          textAlign: 'center',
        }}
      >
        {message}
      </Typography>
    </DialogBase>
  );
};

export default DeleteConfirmDialog;
